// Slide-in cart. Progressive enhancement over plain Shopify forms:
// - product forms (action …/cart/add) are sent with fetch, then the drawer opens with fresh contents
// - the header "Cart (n)" links open the drawer instead of the cart page
// - −/+/remove in the drawer call /cart/change.js
// Drawer HTML comes from the Section Rendering API (sections/pr-cart-drawer.liquid), so Liquid stays the
// single source of truth. Any failure falls back to the normal page flow.
(() => {
  const dialog = document.querySelector('[data-pr-drawer]');
  if (!dialog || typeof dialog.showModal !== 'function' || !window.fetch) return;

  const SECTION = 'pr-cart-drawer';
  const routes = window.routes || {};
  const onCartPage = document.querySelector('main[data-template]')?.dataset.template === 'cart';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let opener = null;

  const body = () => dialog.querySelector('[data-pr-drawer-body]');

  const render = (html) => {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-pr-drawer-body]');
    if (!fresh) return;
    body().replaceChildren(...fresh.childNodes);

    const label = body().querySelector('[data-pr-count-label]');
    if (!label) return;
    document.querySelectorAll('[data-pr-cart-count]').forEach((el) => (el.textContent = label.textContent));
    document.querySelectorAll('[data-pr-cart-link]').forEach((el) => (el.dataset.count = label.dataset.count));
  };

  const open = () => {
    if (dialog.open) return;
    opener = document.activeElement;
    dialog.showModal();
    document.documentElement.classList.add('pr-drawer-open');
    requestAnimationFrame(() => dialog.classList.add('is-open'));
    dialog.querySelector('[data-pr-drawer-close]')?.focus();
  };

  const close = () => {
    if (!dialog.open) return;
    dialog.classList.remove('is-open');
    document.documentElement.classList.remove('pr-drawer-open');
    const done = () => {
      dialog.close();
      opener?.focus?.();
    };
    if (reduceMotion.matches) done();
    else setTimeout(done, 300);
  };

  const status = (text) => {
    const el = dialog.querySelector('[data-pr-drawer-status]');
    if (el) el.textContent = text;
  };

  const post = (url, payload, json) =>
    fetch(url, {
      method: 'POST',
      headers: json
        ? { 'Content-Type': 'application/json', Accept: 'application/json' }
        : { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body: json ? JSON.stringify(payload) : payload,
    }).then(async (response) => {
      const data = await response.json();
      if (!response.ok || data.status) throw data;
      return data;
    });

  const refresh = () =>
    fetch(`${window.location.pathname}?sections=${SECTION}`)
      .then((r) => r.json())
      .then((data) => render(data[SECTION]));

  // Add to cart from any product form.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (onCartPage || !(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add')) return;
    const submitter = event.submitter || form.querySelector('[type="submit"]');
    if (submitter?.name === 'checkout') return;
    event.preventDefault();

    const data = new FormData(form);
    if (submitter?.name && submitter.value) data.set(submitter.name, submitter.value);
    data.append('sections', SECTION);
    data.append('sections_url', window.location.pathname);

    const formId = form.getAttribute('id'); // form.id would return <input name="id">
    const buttons = [...(formId ? document.querySelectorAll(`[type="submit"][form="${formId}"]`) : []), submitter].filter(Boolean);
    buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));

    post(routes.cart_add_url || '/cart/add', data, false)
      .then((response) => {
        render(response.sections?.[SECTION]);
        open();
        status(dialog.dataset.addedLabel || '');
      })
      .catch((error) => {
        refresh().finally(() => {
          open();
          status(error?.description || error?.message || dialog.dataset.errorLabel || '');
        });
      })
      .finally(() => buttons.forEach((b) => b.removeAttribute('aria-busy')));
  });

  // Header / compact bar cart links open the drawer.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-pr-cart-link]');
    if (!link || onCartPage || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    status('');
    open();
  });

  // Quantity / remove inside the drawer.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) return close(); // click on the backdrop
    if (event.target.closest('[data-pr-drawer-close]')) return close();

    const button = event.target.closest('[data-pr-line]');
    if (!button) return;
    body().setAttribute('aria-busy', 'true');
    post(
      routes.cart_change_url ? `${routes.cart_change_url}.js` : '/cart/change.js',
      { line: Number(button.dataset.prLine), quantity: Number(button.dataset.prQty), sections: [SECTION], sections_url: window.location.pathname },
      true
    )
      .then((response) => render(response.sections?.[SECTION]))
      .catch((error) => refresh().finally(() => status(error?.description || error?.message || '')))
      .finally(() => {
        body().removeAttribute('aria-busy');
        dialog.querySelector('[data-pr-drawer-close]')?.focus();
      });
  });

  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });
})();
